/**
 * Wingman API Client
 *
 * Signed-in portal callers pass `serverURL: /api/wingman/{v2|legacy}`
 * (see `signedInWingmanTransport`). The BFF reads the httpOnly session
 * cookie and attaches Authorization: Bearer. Page JavaScript never sees
 * the JWT.
 *
 * Public embeds and server-side callers pass an explicit Wingman
 * `serverURL`. Embeds use `credentials: "omit"` plus X-Org-Id /
 * X-Session-Id. Server-side callers (e.g. Slack) pass `accessToken`.
 *
 * On a 401 from the BFF (stale access token in the cookie), the
 * browser-side `tryRefreshToken()` chokepoint rotates the cookie and we
 * retry the request once. See ADR
 * `docs/adr/0001-centralize-token-refresh-in-middleware.md`.
 */

import { combineAbortSignals, disposeAfterResponse } from "./abort-signals";
import { apiFetchWithRefresh } from "@/lib/api";
import { TOKEN } from "@/types/common/ids";
import { isLoggingOut } from "@/utils/common/logout";
import { WingmanEventStream } from "./stream";
import {
  AppendConversationMessageParams,
  AppendConversationMessageV2Params,
  ConversationDetailsResponse,
  ConversationMessage,
  CreateConversationParams,
  CreateConversationResponse,
  GetConversationParams,
  ListConversationMessagesParams,
  StreamingConversationMessage,
  WingmanRequestOptions,
} from "./types";
import { resolveWingmanRouting } from "./routing";
import { trimTrailingSlash } from "./urls";

// =============================================================================
// Errors
// =============================================================================

/**
 * Bounded byte budget for reading non-2xx response bodies.
 *
 * Two reasons for the cap:
 *   1. Some upstreams (Vercel proxy pages, misconfigured 502s) return multi-MB
 *      HTML error bodies. Reading them in full to truncate later would hold
 *      the whole thing in memory in the browser tab.
 *   2. PostHog event size matters; 500 chars is enough for any reasonable
 *      structured error envelope.
 *
 * Slightly over 2× the char cap because UTF-8 multi-byte characters can
 * inflate byte length without inflating char length, and we'd rather decode
 * a clean prefix than truncate mid-codepoint.
 */
export const RESPONSE_BODY_MAX_LEN = 500;
const RESPONSE_BODY_MAX_BYTES = RESPONSE_BODY_MAX_LEN * 4;

/**
 * The transport-layer failure modes `executeRequest` can produce.
 *
 *   - `http`     — upstream returned a non-2xx response.
 *   - `timeout`  — request hit the client's `timeoutMs` and was aborted.
 *   - `network`  — fetch threw before a response (offline, DNS, TLS, etc).
 *   - `aborted`  — the request was aborted for a reason other than timeout
 *                  (user cancellation via component unmount, or the
 *                  `isLoggingOut()` short-circuit). Aborts are *not* reported
 *                  via `onRequestFailure` — only `http`/`timeout`/`network`
 *                  are traced.
 */
export type WingmanRequestFailureKind =
  | "http"
  | "timeout"
  | "network"
  | "aborted";

/**
 * Shape passed to `WingmanClientOptions.onRequestFailure` for tracing.
 *
 * `status` is the upstream HTTP status for `kind === "http"` and `null`
 * otherwise. `category` is a coarse classification useful for dashboards
 * (e.g. `auth`, `rate_limit`, `payload_too_large`).
 */
export type WingmanRequestFailure = {
  kind: WingmanRequestFailureKind;
  status: number | null;
  category: string;
  url: string;
  method: string;
  responseBody: string;
  apiVersion: WingmanApiVersion;
};

export type WingmanRequestFailureHook = (failure: WingmanRequestFailure) => void;

/**
 * Thrown by `WingmanClient` for any non-success outcome of a request.
 *
 * Callers should branch on `kind` and `status`; the legacy
 * `Wingman API error <status>: <body>` message format is preserved for
 * `kind === "http"` so existing log-grep and `isWingman401Error` matchers
 * keep working.
 */
export class WingmanApiError extends Error {
  readonly kind: WingmanRequestFailureKind;
  readonly status: number | null;
  readonly url: string;
  readonly method: string;
  readonly responseBody: string;

  constructor(opts: {
    kind: WingmanRequestFailureKind;
    status: number | null;
    url: string;
    method: string;
    responseBody: string;
    cause?: unknown;
  }) {
    const message =
      opts.kind === "http"
        ? `Wingman API error ${opts.status}: ${opts.responseBody || "Unknown error"}`
        : `Wingman request ${opts.kind}: ${opts.responseBody || opts.url}`;
    super(message, opts.cause !== undefined ? { cause: opts.cause } : undefined);
    this.name = "WingmanApiError";
    this.kind = opts.kind;
    this.status = opts.status;
    this.url = opts.url;
    this.method = opts.method;
    this.responseBody = opts.responseBody;
  }
}

export { WingmanGenerationError } from "./generation-error";
export type { WingmanGenerationProblemKind } from "./generation-error";

function classifyWingmanFailure(
  kind: WingmanRequestFailureKind,
  status: number | null
): string {
  if (kind === "timeout") return "timeout";
  if (kind === "network") return "network";
  if (kind === "aborted") return "aborted";
  if (status === null) return "other";
  if (status === 401 || status === 403) return "auth";
  if (status === 413) return "payload_too_large";
  if (status === 429) return "rate_limit";
  if (status === 408 || status === 504) return "timeout";
  if (status >= 500) return "upstream_5xx";
  if (status >= 400) return "client_4xx";
  return "other";
}

/**
 * Reads at most `RESPONSE_BODY_MAX_BYTES` from a response body, decodes UTF-8,
 * and slices to the char cap. Cancels the underlying stream so the connection
 * can be released.
 *
 * Falls back to `response.text()` when the body isn't a stream (older runtimes,
 * mocks). Returns `""` if the read fails entirely — never throws.
 */
async function readBoundedErrorBody(response: Response): Promise<string> {
  if (!response.body) {
    try {
      const text = await response.text();
      return text.slice(0, RESPONSE_BODY_MAX_LEN);
    } catch {
      return "";
    }
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;

  try {
    while (received < RESPONSE_BODY_MAX_BYTES) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;

      const remaining = RESPONSE_BODY_MAX_BYTES - received;
      const slice =
        value.byteLength > remaining ? value.subarray(0, remaining) : value;
      chunks.push(slice);
      received += slice.byteLength;
    }
  } catch {
    // partial read; decode what we have
  } finally {
    try {
      await reader.cancel();
    } catch {
      // already closed
    }
  }

  if (received === 0) return "";

  const merged = new Uint8Array(received);
  let offset = 0;
  for (const c of chunks) {
    merged.set(c, offset);
    offset += c.byteLength;
  }

  return new TextDecoder("utf-8", { fatal: false })
    .decode(merged)
    .slice(0, RESPONSE_BODY_MAX_LEN);
}

function isAbortError(e: unknown): boolean {
  if (typeof DOMException !== "undefined" && e instanceof DOMException) {
    return e.name === "AbortError";
  }
  return e instanceof Error && e.name === "AbortError";
}

// =============================================================================
// Client Options
// =============================================================================

// Browser-side default: hit Wingman directly. Cookie domain (.staging-meetgail.com
// in staging) covers ai.staging-meetgail.com, so the `__session*` JWE chunks
// travel with the cross-origin request via `credentials: "include"`; Wingman
// validates the cookie natively. No Next.js proxy or rewrite involved.

export type WingmanApiVersion = "legacy" | "v2";

export interface WingmanClientOptions {
  /**
   * Base URL for the Wingman API.
   *
   * Default: `resolveWingmanRouting(false)` (v2 in Europe, otherwise
   * `NEXT_PUBLIC_WINGMAN_URL`). The browser sends directly to Wingman with
   * `credentials: "include"`; the session cookie carries auth.
   *
   * Override only on the server side, where there is no browser cookie
   * context and callers must supply their own `accessToken` (e.g. Slack
   * handlers using a service token). Pass `apiVersion` with `serverURL` so
   * the contract stays paired with the host.
   */
  serverURL?: string;

  /**
   * Request timeout in milliseconds.
   * @default 30000
   */
  timeoutMs?: number;

  /**
   * Bearer token for direct-to-Wingman calls. Server-side escape hatch only.
   * Signed-in browser callers leave this undefined so the BFF attaches
   * the token from the session cookie.
   */
  accessToken?: TOKEN;

  credentials?: RequestCredentials;

  /**
   * Optional callback invoked once per request failure (HTTP non-2xx, timeout,
   * or network error). Used by chat-product code paths to forward failures to
   * PostHog via `makeWingmanPosthogFailureHook(product)`. Not invoked for
   * user-cancelled aborts or the `isLoggingOut` short-circuit.
   *
   * The hook is called synchronously *before* the matching `WingmanApiError`
   * is thrown. Hook exceptions are caught and logged so tracing can never
   * break a request.
   */
  onRequestFailure?: WingmanRequestFailureHook;

  /**
   * Whether to call `tryRefreshToken()` and retry once on a 401. Defaults
   * to `true` when no `accessToken` is provided (the browser-proxy path)
   * and `false` when `accessToken` is supplied (server-side escape hatch
   * where retry is the caller's responsibility). Override only for
   * specialised paths or in tests.
   */
  refreshOn401?: boolean;

  apiVersion?: WingmanApiVersion;

  /**
   * Legacy only. Base URL for the one legacy request whose body may exceed
   * a function body cap: the streaming chat append
   * (`POST /conversations/{id}/messages`) when it inlines attachments.
   * Everything else keeps using `serverURL`. Defaults to `serverURL`.
   * v2 ignores this; see `attachmentUploads`.
   */
  largeBodyServerURL?: string;

  /**
   * How v2 chat sends carry file attachments.
   *
   * - `"inline"` (default): base64 data URLs inside the append body. Fine
   *   when the client talks to Wingman directly (public embeds, server-side
   *   callers with an `accessToken`).
   * - `"grant"`: for each file, ask Wingman for a single-use upload grant
   *   (`POST /conversations/{id}/document-uploads`, through `serverURL`),
   *   PUT the raw bytes straight to the returned Wingman URL with
   *   `credentials: "omit"`, and send only the resulting document reference
   *   in the append. This is what the signed-in BFF transport uses: the
   *   append stays small enough for a Vercel function, the bytes never cross
   *   one, and no cookie or token travels with them. See ADR 0008.
   */
  attachmentUploads?: "inline" | "grant";
}

/**
 * Per-request options shared by every Wingman call. `rawHeaders` replaces
 * the standard header set entirely and `credentials` overrides the client
 * default; both exist for grant uploads only.
 */
type WingmanFetchOptions = RequestInit & {
  xOrgId: string;
  additionalHeaders?: Record<string, string>;
  rawHeaders?: Record<string, string>;
};

// =============================================================================
// Wingman Client
// =============================================================================

export class WingmanClient {
  private readonly baseUrl: string;
  private readonly largeBodyBaseUrl: string;
  private readonly timeoutMs: number;
  private readonly accessToken?: TOKEN;
  private readonly credentials?: RequestCredentials;
  private readonly onRequestFailure?: WingmanRequestFailureHook;
  private readonly refreshOn401: boolean;
  private readonly apiVersion: WingmanApiVersion;
  private readonly attachmentUploads: "inline" | "grant";

  constructor(options: WingmanClientOptions = {}) {
    const fallback = resolveWingmanRouting(false);
    const url = options.serverURL ?? fallback.serverURL;
    this.baseUrl = trimTrailingSlash(url);
    this.largeBodyBaseUrl = options.largeBodyServerURL
      ? trimTrailingSlash(options.largeBodyServerURL)
      : this.baseUrl;
    this.timeoutMs = options.timeoutMs ?? 30000;
    this.accessToken = options.accessToken;
    this.credentials = options.credentials;
    this.onRequestFailure = options.onRequestFailure;
    this.refreshOn401 = options.refreshOn401 ?? options.accessToken === undefined;
    // An explicit serverURL without apiVersion is the historical escape hatch
    // (legacy host). Do not apply the region v2 default to a caller-supplied URL.
    this.apiVersion =
      options.apiVersion ??
      (options.serverURL === undefined ? fallback.apiVersion : "legacy");
    this.attachmentUploads = options.attachmentUploads ?? "inline";
  }

  private buildHeaders(
    xOrgId: string,
    additionalHeaders?: Record<string, string>
  ): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "X-Org-Id": xOrgId,
      ...additionalHeaders,
    };

    if (this.accessToken && this.accessToken.length > 0) {
      headers["Authorization"] = `Bearer ${this.accessToken}`;
    }

    return headers;
  }

  private resolveFetchCredentials(): RequestCredentials | undefined {
    if (this.accessToken) {
      return undefined;
    }
    return this.credentials ?? "include";
  }

  private reportFailure(failure: WingmanRequestFailure): void {
    if (!this.onRequestFailure) return;
    try {
      this.onRequestFailure(failure);
    } catch (e) {
      console.warn("[WingmanClient] onRequestFailure threw", e);
    }
  }

  /**
   * Shared request executor with timeout. Signed-in browser calls hit
   * `/api/wingman/*` with `credentials: "include"` so the BFF can read
   * the session cookie and attach Authorization: Bearer.
   *
   * On a 401 from the proxy (browser path, `refreshOn401: true`) the
   * client calls `tryRefreshToken()` — which hits `/api/auth/refresh` and
   * is intra- and inter-tab coalesced — and retries the request once. If
   * the retry also returns 401, the original 401 is surfaced as a
   * `WingmanApiError` with `kind: "http"`, and the `AuthProvider`
   * session-expired modal kicks in via `notifySessionExpired()` from
   * inside `tryRefreshToken`.
   *
   * Failures (HTTP non-2xx, timeout, network) are reported via
   * `onRequestFailure` exactly once, then a `WingmanApiError` is thrown.
   * Aborts (user cancellation, `isLoggingOut()` short-circuit) also throw
   * `WingmanApiError` with `kind: "aborted"` but are *not* traced.
   */
  private async executeRequest(
    url: string,
    options: WingmanFetchOptions,
    extraHeaders?: Record<string, string>
  ): Promise<Response> {
    const method = options.method ?? "GET";
    // Delegate the send + on-401-refresh-and-retry recipe to the shared
    // helper so it stays in lockstep with `apiFetchRaw` (per ADR 0002).
    // `fetchOnce` owns timeout / abort / header construction; it's the
    // re-runnable thunk the recipe needs.
    const response = await apiFetchWithRefresh(
      () => this.fetchOnce(url, options, extraHeaders),
      { retryOn401: this.refreshOn401 }
    );
    return this.handleResponseOrThrow(response, url, method);
  }

  private async fetchOnce(
    url: string,
    options: WingmanFetchOptions,
    extraHeaders?: Record<string, string>
  ): Promise<Response> {
    if (isLoggingOut()) {
      throw new WingmanApiError({
        kind: "aborted",
        status: null,
        url,
        method: options.method ?? "GET",
        responseBody: "logout in progress",
      });
    }

    // A grant upload sends exactly the headers it is given: no JSON content
    // type, no X-Org-Id, and no credential of any kind. Everything else gets
    // the standard set.
    const headers = options.rawHeaders ?? {
      ...this.buildHeaders(options.xOrgId, options.additionalHeaders),
      ...extraHeaders,
    };
    const method = options.method ?? "GET";

    const controller = new AbortController();
    const { signal, dispose } = combineAbortSignals(
      options.signal ? [controller.signal, options.signal] : [controller.signal]
    );
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, this.timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal,
        credentials: options.credentials ?? this.resolveFetchCredentials(),
      });
      return disposeAfterResponse(response, dispose);
    } catch (e) {
      dispose?.();
      if (signal.aborted || isAbortError(e)) {
        if (timedOut) {
          const failure: WingmanRequestFailure = {
            kind: "timeout",
            status: null,
            category: classifyWingmanFailure("timeout", null),
            url,
            method,
            responseBody: `timed out after ${this.timeoutMs}ms`,
            apiVersion: this.apiVersion,
          };
          this.reportFailure(failure);
          throw new WingmanApiError({ ...failure, cause: e });
        }
        // Non-timeout abort: treat as user cancellation; do not trace.
        throw new WingmanApiError({
          kind: "aborted",
          status: null,
          url,
          method,
          responseBody: "request aborted",
          cause: e,
        });
      }

      const message = e instanceof Error ? e.message : String(e);
      const failure: WingmanRequestFailure = {
        kind: "network",
        status: null,
        category: classifyWingmanFailure("network", null),
        url,
        method,
        responseBody: message.slice(0, RESPONSE_BODY_MAX_LEN),
        apiVersion: this.apiVersion,
      };
      this.reportFailure(failure);
      throw new WingmanApiError({ ...failure, cause: e });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private async handleResponseOrThrow(
    response: Response,
    url: string,
    method: string
  ): Promise<Response> {
    if (response.ok) return response;

    const responseBody = await readBoundedErrorBody(response);
    const failure: WingmanRequestFailure = {
      kind: "http",
      status: response.status,
      category: classifyWingmanFailure("http", response.status),
      url,
      method,
      responseBody: responseBody || "Unknown error",
      apiVersion: this.apiVersion,
    };
    this.reportFailure(failure);
    throw new WingmanApiError(failure);
  }

  private async fetch<T>(url: string, options: WingmanFetchOptions): Promise<T> {
    const response = await this.executeRequest(url, options);
    return response.json() as Promise<T>;
  }

  private async fetchStream(url: string, options: WingmanFetchOptions): Promise<Response> {
    return this.executeRequest(url, options, { Accept: "text/event-stream" });
  }

  /**
   * PUT the raw bytes of one attachment to the Wingman URL named by an
   * upload grant. The grant is the whole credential: no cookie, no bearer,
   * no X-Org-Id, and no refresh-and-retry (a 401 here means the grant was
   * bad or spent, not that the session is). Timeout, abort, and failure
   * reporting are the same as every other Wingman request.
   */
  private async putDocumentUpload(
    url: string,
    upload: { body: Uint8Array; mediaType: string; grant: string; signal?: AbortSignal }
  ): Promise<{ documentId: string; url: string }> {
    const response = await this.fetchOnce(url, {
      method: "PUT",
      body: upload.body as BodyInit,
      xOrgId: "",
      rawHeaders: { "Content-Type": upload.mediaType, "X-Upload-Grant": upload.grant },
      credentials: "omit",
      signal: upload.signal,
    });
    const ok = await this.handleResponseOrThrow(response, url, "PUT");
    return ok.json() as Promise<{ documentId: string; url: string }>;
  }

  private createV2Transport() {
    return {
      baseUrl: this.baseUrl,
      attachmentUploads: this.attachmentUploads,
      fetchJson: <T>(url: string, options: WingmanFetchOptions) => this.fetch<T>(url, options),
      fetchStream: (url: string, options: WingmanFetchOptions) => this.fetchStream(url, options),
      putDocumentUpload: (url: string, upload: Parameters<WingmanClient["putDocumentUpload"]>[1]) =>
        this.putDocumentUpload(url, upload),
    };
  }

  conversations = {
    /**
     * POST /conversations
     */
    createConversation: async (
      params: CreateConversationParams,
      options?: WingmanRequestOptions
    ): Promise<CreateConversationResponse> => {
      const url = `${this.baseUrl}/conversations`;

      return this.fetch<CreateConversationResponse>(url, {
        method: "POST",
        body: JSON.stringify(params.createConversationRequest),
        xOrgId: params.xOrgId,
        additionalHeaders: options?.headers,
      });
    },

    /**
     * GET /conversations/{conversationId}
     */
    getConversation: async (
      params: GetConversationParams,
      options?: WingmanRequestOptions
    ): Promise<ConversationDetailsResponse> => {
      const url = `${this.baseUrl}/conversations/${params.conversationId}`;

      return this.fetch<ConversationDetailsResponse>(url, {
        method: "GET",
        xOrgId: params.xOrgId,
        additionalHeaders: options?.headers,
        signal: options?.signal,
      });
    },

    /**
     * GET /conversations/{conversationId}/messages
     */
    listConversationMessages: async (
      params: ListConversationMessagesParams,
      options?: WingmanRequestOptions
    ): Promise<ConversationMessage[]> => {
      const url = `${this.baseUrl}/conversations/${params.conversationId}/messages`;

      if (this.apiVersion === "v2") {
        const { listConversationMessagesV2 } = await import("./v2-adapter");
        return listConversationMessagesV2({
          transport: this.createV2Transport(),
          params,
          options,
        });
      }

      return this.fetch<ConversationMessage[]>(url, {
        method: "GET",
        xOrgId: params.xOrgId,
        additionalHeaders: options?.headers,
        signal: options?.signal,
      });
    },

    /**
     * POST /conversations/{conversationId}/messages (streaming)
     */
    appendConversationMessage: async (
      params: AppendConversationMessageParams,
      options?: WingmanRequestOptions
    ): Promise<WingmanEventStream<StreamingConversationMessage>> => {
      // Legacy answers the append itself as an SSE stream with the files
      // inlined, so the whole exchange rides the large-body base. v2 never
      // needs one: the adapter uploads attachments under a grant first.
      const url = `${this.largeBodyBaseUrl}/conversations/${params.conversationId}/messages`;

      if (this.apiVersion === "v2") {
        const { appendConversationMessageV2 } = await import("./v2-adapter");
        return appendConversationMessageV2({
          transport: this.createV2Transport(),
          params,
          options,
        });
      }

      const response = await this.fetchStream(url, {
        method: "POST",
        body: JSON.stringify(params.appendConversationMessageRequest),
        xOrgId: params.xOrgId,
        additionalHeaders: options?.headers,
        signal: options?.signal,
      });

      return new WingmanEventStream<StreamingConversationMessage>(response);
    },

    /**
     * POST /v2/conversations/{conversationId}/messages
     */
    appendConversationMessageV2: async (
      params: AppendConversationMessageV2Params,
      options?: WingmanRequestOptions
    ): Promise<ConversationMessage> => {
      const url = `${this.baseUrl}/v2/conversations/${params.conversationId}/messages`;

      return this.fetch<ConversationMessage>(url, {
        method: "POST",
        body: JSON.stringify(params.appendConversationMessageV2Request),
        xOrgId: params.xOrgId,
        additionalHeaders: options?.headers,
      });
    },
  };
}
